import Layout from '@/components/Layout'
import '@/utils/axios'
import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import { MantineProvider, createTheme } from '@mantine/core'
import '@mantine/core/styles.css'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { StrictMode, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { Toaster, toast } from 'react-hot-toast'

dayjs.locale('fr')

function hasMessage(data: unknown): data is { message: string } {
  return typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string'
}

function hasError(error: unknown): error is { error: string } {
  return typeof error === 'object' && error !== null && 'error' in error && typeof error.error === 'string'
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      gcTime: Infinity,
    },
    mutations: {
      onSuccess: (data: unknown) => {
        if (hasMessage(data)) {
          toast.success(data.message)
        }
      },
      onError: (error: unknown) => {
        if (hasError(error)) {
          toast.error(error.error)
        }
      },
    },
  },
})

const fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

// No primaryColor override: the brand is Mantine's stock blue, mirrored on the landing page
// (app/frontend/pages/pages/landing.module.css) and in emails (app/lib/app_config.rb).
const theme = createTheme({
  defaultRadius: 'md',
  fontFamily,
  headings: { fontFamily, fontWeight: '700' },
})

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>('../pages/**/*.tsx', {
      eager: true,
    })
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // Use default layout for all pages
    // see https://inertia-rails.dev/guide/pages#default-layouts
    page.default.layout ||= (page: ReactNode) => <Layout>{page}</Layout>

    return page
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <MantineProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <Toaster position="top-center" containerClassName="no-print" />
            <App {...props} />
          </QueryClientProvider>
        </MantineProvider>
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.'
    )
  }
})
